// frontend/src/utils/validateDemoIdea.ts
// Client-side validation for demo ideas - mirrors backend validation

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validates demo idea on client side (mirrors backend validation)
 * This provides immediate feedback before API call
 */
export function validateDemoIdea(idea: string): ValidationResult {
  // Check if idea exists and is a string
  if (!idea || typeof idea !== 'string') {
    return {
      valid: false,
      error: 'Please enter your business idea'
    };
  }

  // Check if idea has meaningful content
  const trimmed = idea.trim();
  if (!trimmed || trimmed.length < 3) {
    return {
      valid: false,
      error: 'Idea must be at least 3 characters long'
    };
  }

  // Check maximum length
  if (trimmed.length > 2000) {
    return {
      valid: false,
      error: 'Idea is too long. Please keep it under 2000 characters'
    };
  }

  // Check for placeholder text
  const normalized = trimmed.toLowerCase();
  const placeholderPatterns = [
    'enter your idea',
    'enter your business idea',
    'placeholder',
    'test idea',
    'example',
    'sample idea',
    'type here',
    'your idea here',
  ];

  const isPlaceholder = placeholderPatterns.some(pattern => 
    normalized === pattern || 
    normalized === pattern + '.' || 
    normalized === pattern + '...'
  );

  if (isPlaceholder) {
    return {
      valid: false,
      error: 'Please enter your actual business idea instead of placeholder text'
    };
  }

  // Check for generic single words
  const genericWords = ['test', 'demo', 'example', 'sample', 'placeholder', 'idea'];
  if (trimmed.split(/\s+/).length === 1 && genericWords.includes(normalized)) {
    return {
      valid: false,
      error: 'Please provide a more specific business idea description'
    };
  }

  // ⚠️ CONTENT SAFETY: Check for harmful/illegal content patterns
  // Note: Client-side check is for UX only - server ALWAYS validates
  
  // CRITICAL words that are always blocked (strict matching)
  const criticalWords = [
    // Sexual content
    'porn', 'pornography', 'xxx', 'nsfw', 'prostitution',
    'escort', 'brothel', 'stripper', 'trafficking', 'traffick',
    'sex work', 'camgirl',
    
    // Child safety
    'pedophilia', 'csam', 'child porn', 'child abuse', 'underage',
    'grooming', 'loli', 'lolita',
    
    // Drugs & violence
    'cocaine', 'heroin', 'meth', 'hitman', 'assassination',
    'drug dealing', 'narcotics', 'cartel', 'mafia', 'mob',
    'organized crime',
    
    // Sexual violence (ZERO TOLERANCE)
    'rape', 'sexual assault', 'sexual abuse', 'molest', 'molestation',
    'date rape', 'roofie',
    
    // Hate & terror
    'nazi', 'terrorist', 'terrorism', 'isis', 'jihadist',
    
    // Fraud
    'ponzi', 'pyramid scheme', 'money laundering', 'counterfeit',
    'scam', 'fraud', 'phishing',
  ];
  
  // Criminal context words (blocked when combined with business terms)
  const criminalContextWords = [
    'criminal', 'illegal', 'unlicensed', 'underground', 'black market',
    'dark web', 'darknet', 'stolen', 'fake id', 'forged'
  ];
  
  // Weapons context words (blocked when combined with illegal/selling/trafficking)
  const weaponsContextWords = [
    'gun', 'guns', 'firearm', 'firearms', 'weapon', 'weapons',
    'rifle', 'pistol', 'ammunition', 'ammo', 'explosives'
  ];
  
  // Additional context-based harmful phrases
  const harmfulPhrases = [
    'sex traffic', 'human trafficking', 'organ trafficking',
    'child exploitation', 'illegal weapon', 'bomb making',
    'fraud scheme', 'crypto scam', 'fake vaccine',
    'identity theft', 'stolen credit', 'hacking service',
    'malware', 'ransomware', 'suicide method', 'self harm',
  ];

  // Check critical words with word boundaries
  const foundCritical = criticalWords.find(word => {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    return regex.test(normalized);
  });
  
  // Check criminal context (only when combined with business terms)
  const foundCriminalContext = criminalContextWords.find(word => {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    if (regex.test(normalized)) {
      // Check if combined with business-related terms
      const businessContext = /\b(business|organization|operation|network|service|platform|marketplace|app|website|company|startup|venture)\b/i;
      return businessContext.test(normalized);
    }
    return false;
  });
  
  // Check weapons context (only when combined with illegal/selling/trafficking)
  const foundWeaponsContext = weaponsContextWords.find(word => {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    if (regex.test(normalized)) {
      // Check if combined with harmful intent keywords
      const harmfulIntent = /\b(illegal|unlicensed|black market|dark web|trafficking|smuggling|dealing|dealer|sell|sale|sales|selling|trade|trading|underground|stolen)\b/i;
      return harmfulIntent.test(normalized);
    }
    return false;
  });
  
  // Check harmful phrases
  const foundPhrase = harmfulPhrases.find(phrase => 
    normalized.includes(phrase)
  );
  
  const foundHarmful = foundCritical || foundCriminalContext || foundWeaponsContext || foundPhrase;

  if (foundHarmful) {
    return {
      valid: false,
      error: 'This platform cannot be used to validate illegal, harmful, or inappropriate business ideas. Please submit a legitimate business concept.'
    };
  }

  // Additional check for spam patterns
  const spamPatterns = [
    /(.)\1{10,}/, // Repeated characters
    /^\d+$/, // Only numbers
    /^[^a-zA-Z]+$/, // No letters at all
  ];

  if (spamPatterns.some(pattern => pattern.test(trimmed))) {
    return {
      valid: false,
      error: 'Please enter a valid business idea description'
    };
  }

  return { valid: true };
}

/**
 * Get a user-friendly suggestion based on what went wrong
 */
export function getValidationSuggestion(error: string): string {
  if (error.includes('placeholder')) {
    return 'Try: "AI-powered fitness app for seniors" or "Sustainable food delivery service"';
  }
  if (error.includes('3 characters')) {
    return 'Describe your business idea in a few words';
  }
  if (error.includes('too long')) {
    return 'Keep it concise - focus on the core concept';
  }
  if (error.includes('illegal') || error.includes('harmful')) {
    return 'We only analyze legitimate, lawful business concepts';
  }
  if (error.includes('specific')) {
    return 'Add more details about your business concept';
  }
  return 'Please provide a clear business idea description';
}

/**
 * Check if idea seems like it needs location context
 */
export function suggestsLocationNeeded(idea: string): boolean {
  const locationKeywords = [
    'local', 'city', 'neighborhood', 'community', 'regional',
    'restaurant', 'cafe', 'retail', 'store', 'shop',
    'delivery', 'service area', 'marketplace'
  ];

  const normalized = idea.toLowerCase();
  return locationKeywords.some(keyword => normalized.includes(keyword));
}

/**
 * Estimate idea quality/completeness (for UX hints)
 */
export function estimateIdeaQuality(idea: string): {
  score: number; // 0-100
  feedback: string;
} {
  const trimmed = idea.trim();
  let score = 0;
  const feedback: string[] = [];

  // Length check
  if (trimmed.length > 20) score += 20;
  else feedback.push('Add more detail');

  if (trimmed.length > 50) score += 10;

  // Word count check
  const words = trimmed.split(/\s+/).length;
  if (words >= 3) score += 20;
  else feedback.push('Use 3+ words');

  if (words >= 5) score += 15;
  if (words >= 8) score += 10;

  // Has target audience indicator
  if (/\bfor\b|\btarget|\bcustomer|\buser/i.test(trimmed)) {
    score += 15;
  } else {
    feedback.push('Specify target audience');
  }

  // Has action/value proposition
  if (/\bapp\b|\bplatform\b|\bservice\b|\bproduct\b|\btool\b|\bsolution\b/i.test(trimmed)) {
    score += 10;
  } else {
    feedback.push('Describe what it does');
  }

  // Bonus for good structure
  if (words >= 5 && words <= 15) score += 10; // Sweet spot

  return {
    score: Math.min(100, score),
    feedback: feedback.join(', ') || 'Good description!'
  };
}
